import React, { Suspense, useEffect, useState } from "react";

import homePagePayload from "../../config/homePayload";
import homePageAPI from "../../api/homePageApi";

import CouponFlash from "./CouponFlash";
import { Link } from "react-router-dom";
import { Row, Col, Container } from "react-bootstrap";

import {
  Swiper,
  SwiperSlide,
  Autoplay,
  Navigation,
  Pagination,
} from "./Shared/CustomSwiper";
import { FaBox, FaCheckCircle, FaHeadset } from "react-icons/fa";

const Product = React.lazy(() => import("../products/Card"));
const Testimonials = React.lazy(() => import("../testimonials/Card"));
const CategoryCard = React.lazy(() => import("../categories/Card"));
const Blog = React.lazy(() => import("../blogs/Card"));
const Banner = React.lazy(() => import("./Banner"));

function Home() {
  const [coupons, setCoupons] = useState([]);
  const [categories, setCategories] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [products, setProducts] = useState([]);
  const [topSellerProducts, setTopSellerProducts] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [topBanners, setTopBanners] = useState([]);
  const [middleBanners, setMiddleBanners] = useState([]);
  const firstMiddleBanner = middleBanners[0];
  const secondMiddleBanner = middleBanners[1];

  useEffect(() => {
    fetchHomePageDetails();
  }, []);

  const CACHE_TIME = 3 * 60 * 1000;

  const applyData = (data) => {
    setTopBanners(data?.banners?.filter((b) => b.position === "top"));
    setMiddleBanners(data?.banners?.filter((b) => b.position === "middle"));
    setCoupons(data?.coupons);
    setCategories(data?.categories);
    setTestimonials(data?.testimonials);
    setBlogs(data?.blogs);
    setProducts(data?.products);
    setTopSellerProducts(data?.topSellerProducts);
  };

  const fetchHomePageDetails = async () => {
    try {
      const cached = sessionStorage.getItem("homeData");
      const parsed = cached ? JSON.parse(cached) : null;

      if (parsed) applyData(parsed.data);

      if (!parsed || Date.now() - parsed.timestamp > CACHE_TIME) {
        const res = await homePageAPI.homePage(homePagePayload);
        if (res.code === 200) {
          sessionStorage.setItem(
            "homeData",
            JSON.stringify({ data: res.data, timestamp: Date.now() }),
          );
          if (!parsed) applyData(res.data); // only update state if no cache was shown
        }
      }
    } catch (error) {
      console.error("Home API Error:", error);
    }
  };

  return (
    <>
      {/* show banners */}
      {topBanners && topBanners.length > 0 && (
        <Swiper
          modules={[Navigation, Pagination, Autoplay]}
          slidesPerView={1}
          pagination={{ clickable: true }}
          autoplay={{ delay: 3000 }}
          loop={true}
        >
          {topBanners.map((banner, index) => (
            <SwiperSlide key={index}>
              <Banner item={banner} />
            </SwiperSlide>
          ))}
        </Swiper>
      )}

      {/* Coupon Bar */}
      {coupons && coupons.length > 0 && (
        <div className="coupon-section py-4">
          <div className="container">
            <Suspense fallback={<div></div>}>
              {coupons.map((coupon, index) => (
                <React.Fragment key={index}>
                  <CouponFlash item={coupon} />
                </React.Fragment>
              ))}
            </Suspense>
          </div>
        </div>
      )}

      {/* category listing */}
      {categories && categories.length > 0 && (
        <section className="custom-category-section main-section">
          <div className="container">
             <div className="d-flex align-items-center justify-content-between mb-2">
              <h2 className="section-title ">Shop by Category</h2>
              <Link to="/categories" className="view-all-btn">
                View All
              </Link>
            </div>

            <Swiper
              modules={[Navigation, Pagination, Autoplay]}
              slidesPerView={1}
              pagination={{ clickable: true }}
              autoplay={{ delay: 3000 }}
              spaceBetween={20}
              breakpoints={{
                0: { slidesPerView: 1.2 },
                768: { slidesPerView: 2 },
                1024: { slidesPerView: 3 },
              }}
              loop={true}
            >
              {categories.map((category, index) => (
                <SwiperSlide key={index}>
                  <CategoryCard item={category} />
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </section>
      )}

      {/* bestsellers */}
      {products && products.length > 0 && (
        <section className="bestsellers-section main-section">
          <div className="container ">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <h2 className="section-title">Bestsellers</h2>
              <Link to="/shop" className="view-all-btn">
                View All
              </Link>
            </div>
            <Row className="g-3">
              <Suspense fallback={<div></div>}>
                {products.map((product, index) => (
                  <Col lg={3} md={6} sm={6} xs={6} key={[product._id || index]}>
                    <Product item={product} />
                  </Col>
                ))}
              </Suspense>
            </Row>
          </div>
        </section>
      )}

      {/* middle banners */}
      {firstMiddleBanner && (
        <section
          className="suit-banner"
          style={{
            backgroundImage: `url(${firstMiddleBanner.image})`,
          }}
        >
          <div className="container">
            <div className="suit-banner-content">
              <h2 className="suit-banner-title">{firstMiddleBanner.title}</h2>

              <p className="suit-banner-subtitle">
                {firstMiddleBanner.subTitle}
              </p>

              <Link to="/shop" className="suit-banner-btn">
                Shop Now
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* trending */}
      {topSellerProducts && topSellerProducts.length > 0 && (
        <section className="bestsellers-section main-section mt-4">
          <div className="container ">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <h2 className="section-title ">Trending Products</h2>
              <Link to="/shop" className="view-all-btn">
                View All
              </Link>
            </div>
            <Row className="g-3">
              <Suspense fallback={<div></div>}>
                {topSellerProducts.map((product, index) => (
                  <Col lg={3} md={6} sm={6} xs={6} key={[product._id || index]}>
                    <Product item={product} />
                  </Col>
                ))}
              </Suspense>
            </Row>
          </div>
        </section>
      )}

      {/* category cta section */}
      {secondMiddleBanner && (
        <section className="cta-banner-2 py-5">
          <Container>
            <Row className="align-items-center">
              {/* LEFT IMAGE */}
              <Col md={7} className="mb-4 mb-md-0">
                <div className="banner-media-wrapper">
                  <img
                    src={secondMiddleBanner.image}
                    alt={secondMiddleBanner.title}
                    className="banner-media"
                    loading="lazy"
                  />
                </div>
              </Col>

              {/* RIGHT CONTENT */}
              <Col md={5}>
                <div className="banner-content-wrapper p-3 p-md-5">
                  {/* TITLE */}
                  <h2 className="banner-heading">{secondMiddleBanner.title}</h2>

                  {/* SUBTITLE */}
                  <p className="banner-description">
                    {secondMiddleBanner.subTitle ||
                      secondMiddleBanner.sub_title}
                  </p>

                  {/* CATEGORY SLIDER */}
                  <Swiper
                    spaceBetween={0}
                    slidesPerView={3}
                    loop
                    modules={[Navigation, Autoplay]}
                    breakpoints={{
                      0: { slidesPerView: 2.2 },
                      768: { slidesPerView: 2 },
                      1024: { slidesPerView: 3 },
                    }}
                    className="category-swiper"
                    autoplay={{ delay: 2500 }}
                  >
                    <Suspense fallback={<div></div>}>
                      {categories.map((cat, index) => (
                        <SwiperSlide key={index}>
                          <Link
                            to={`/categories/${cat.slug}`}
                            className="category-item"
                          >
                            <div className="category-circle">
                              <img
                                src={cat.image}
                                alt={cat.title || cat.name}
                                className="img-fluid"
                                loading="lazy"
                              />
                            </div>
                            <span>{cat.title || cat.name}</span>
                          </Link>
                        </SwiperSlide>
                      ))}
                    </Suspense>
                  </Swiper>
                </div>
              </Col>
            </Row>
          </Container>
        </section>
      )}

      {testimonials && testimonials.length > 0 && (
        <section className="testimonials-section py-5">
          <Container>
            <div className="text-left mb-2">
              <h2 className="section-title">What Our Clients Say</h2>
            </div>

            <Swiper
              modules={[Pagination, Autoplay]}
              spaceBetween={10}
              slidesPerView={1}
              pagination={true}
              autoplay={{ delay: 2500 }}
              breakpoints={{
                0: { slidesPerView: 1.8 },
                768: { slidesPerView: 3 },
                1024: { slidesPerView: 4 },
              }}
              className="testimonial-swiper"
            >
              <Suspense fallback={<div></div>}>
                {testimonials.map((testimonial, index) => (
                  <SwiperSlide key={index}>
                    <Testimonials item={testimonial} />
                  </SwiperSlide>
                ))}
              </Suspense>
            </Swiper>
          </Container>
        </section>
      )}

      {blogs && blogs.length > 0 && (
        <section className="blog-section py-5">
          <Container>
            <div className="d-flex justify-content-between align-items-end mb-2">
              <div>
                <h2 className="mt-2 mb-0 section-title">Latest Stories</h2>
              </div>
              <Link to="/blog" className="view-all-link">
                View All Blogs
              </Link>
            </div>

            <Swiper
              modules={[Pagination, Autoplay]}
              spaceBetween={30}
              slidesPerView={1}
              // autoplay={{ delay: 3500 }}
              pagination={{ clickable: true }}
              breakpoints={{
                768: { slidesPerView: 2 },
                1024: { slidesPerView: 3 },
              }}
              className="blog-swiper"
            >
              <Suspense fallback={<div></div>}>
                {blogs.map((blog, index) => (
                  <SwiperSlide key={blog.id}>
                    <Blog item={blog} />
                  </SwiperSlide>
                ))}
              </Suspense>
            </Swiper>
          </Container>
        </section>
      )}

      {/* featured section */}
      {/* <div className="sanwar-feature-section">
        <div className="container">
          <div className="row g-4">
            <div className="col-md-4 col-12">
              <div className="sanwar-feature-box text-center">
                <FaBox size={24} className="sanwar-feature-icon" />
                <h6 className="sanwar-feature-title">Shipping Across India</h6>
                <p>
                  Reliable delivery to your doorstep with standard shipping
                  charges applied at checkout.
                </p>
              </div>
            </div>

            <div className="col-md-4 col-12">
              <div className="sanwar-feature-box text-center">
                <FaCheckCircle size={24} className="sanwar-feature-icon" />
                <h6 className="sanwar-feature-title">Return & Exchange</h6>
                <p>
                  Hassle-free exchanges on eligible products. Shop with
                  confidence knowing we’ve got you covered.
                </p>
              </div>
            </div>

            <div className="col-md-4 col-12">
              <div className="sanwar-feature-box text-center">
                <FaHeadset size={24} className="sanwar-feature-icon" />
                <h6 className="sanwar-feature-title">Support Online</h6>
                <p>
                  Our team is ready to assist you anytime via chat, email, or
                  call. Get help whenever you need it.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div> */}
    </>
  );
}

export default Home;
